import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { KfProfileCard } from "../components/knightsfee/KfProfileCard";
import { KfPostCard } from "../components/knightsfee/KfPostCard";
import { GauntletsPanel } from "../components/knightsfee/GauntletsPanel";
import { RankLadderPanel } from "../components/knightsfee/RankLadderPanel";
import { BattleLogPanel } from "../components/knightsfee/BattleLogPanel";
import { MerchantDashboardWidget } from "../components/bazaar/MerchantDashboardWidget";
import { ArrowLeft, Scroll } from "lucide-react";

export default function KnightsFeeProfilePage() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [gauntlets, setGauntlets] = useState(null);
  const [merchantDashboard, setMerchantDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setGauntlets(null);
    setMerchantDashboard(null);
    Promise.all([
      api.get(`/kf/profile/${userId}`),
      api.get("/kf/feed", { params: { author_id: userId } }),
      api.get(`/gauntlets/user/${userId}`),
    ])
      .then(([profileRes, feedRes, gauntletsRes]) => {
        setProfile(profileRes.data);
        setPosts(feedRes.data);
        setGauntlets(gauntletsRes.data);
        if (profileRes.data.is_self && profileRes.data.is_merchant) {
          api.get("/bazaar/merchant/dashboard").then(({ data }) => setMerchantDashboard(data)).catch(() => {});
        }
      })
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <div className="min-h-screen bg-[#151613] text-[#f1e0c5]" data-testid="knights-fee-profile-page">
      <nav className="flex items-center gap-4 px-6 md:px-12 py-6 border-b border-white/5">
        <button data-testid="kf-profile-back-button" onClick={() => navigate("/knights-fee")} className="text-[#f1e0c5]/50 hover:text-[#D4AF37]">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <span className="font-display text-2xl flex items-center gap-2">
          <Scroll className="w-6 h-6 text-[#D4AF37]" /> Knights Fee
        </span>
      </nav>

      <div className="px-6 md:px-12 py-8 max-w-2xl mx-auto space-y-6">
        {loading ? (
          <p className="text-sm text-[#f1e0c5]/40 py-10 text-center">Loading this Knight's profile…</p>
        ) : !profile ? (
          <p className="text-sm text-[#f1e0c5]/40 py-10 text-center">Couldn't find that Knight.</p>
        ) : (
          <>
            <KfProfileCard
              profile={profile}
              onProfileUpdate={setProfile}
              onFollowChange={setProfile}
              gauntletBadge={gauntlets?.badge_display ? {
                tier: gauntlets.badge_display.raid_type === "total" ? gauntlets.total_tier : gauntlets.gauntlets[gauntlets.badge_display.raid_type]?.tier,
                corner: gauntlets.badge_display.corner,
                label: gauntlets.badge_display.raid_type === "total" ? "Total Gauntlet" : gauntlets.gauntlets[gauntlets.badge_display.raid_type]?.label,
              } : null}
              ranks={gauntlets?.ranks}
            />
            {merchantDashboard && <MerchantDashboardWidget dashboard={merchantDashboard} />}
            {gauntlets && <GauntletsPanel summary={gauntlets} isSelf={profile.is_self} onUpdate={(badge) => setGauntlets((g) => ({ ...g, badge_display: badge }))} />}
            {gauntlets && <RankLadderPanel ranks={gauntlets.ranks} isSelf={profile.is_self} onUpdate={(ranks) => setGauntlets((g) => ({ ...g, ranks }))} />}
            {profile.is_self && <BattleLogPanel />}
            <div className="space-y-4">
              {posts.length === 0 ? (
                <p className="text-sm text-[#f1e0c5]/40 text-center py-8" data-testid="kf-profile-no-posts">Nothing posted yet.</p>
              ) : (
                posts.map((post) => <KfPostCard key={post.id} post={post} myUserId={user?.id} onDeleted={(id) => setPosts((prev) => prev.filter((p) => p.id !== id))} />)
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
